import { usePermissions } from "../../hooks/usePermissions";
import styles from "./PermissionBanner.module.css";

type PermissionBannerProps = {
  scope?: "taxonomy" | "fulfilment" | "users" | "submissions";
  resource?: "subscriptions" | "packages" | "features";
};

type PermissionConfig = {
  read: string | string[];
  create: string | string[] | boolean;
  edit: string | string[] | boolean;
};

type MessageConfig = {
  readOnly: string;
  limited?: string;
};

export default function PermissionBanner({ scope = "taxonomy", resource }: PermissionBannerProps) {
  const { hasPermission, hasAnyPermission } = usePermissions();

  const permissionConfig: Record<string, PermissionConfig> = {
    taxonomy: {
      read: "taxonomy.read",
      create: "taxonomy.write",
      edit: "taxonomy.delete",
    },
    users: {
      read: "user.read_accounts",
      create: false,
      edit: false,
    },
    "fulfilment:subscriptions": {
      read: "fulfilment.read_subscriptions",
      create: "fulfilment.create_subscription",
      edit: "fulfilment.edit_subscription",
    },
    "fulfilment:packages": {
      read: "fulfilment.read_packages",
      create: "fulfilment.create_package",
      edit: "fulfilment.create_package",
    },
    "fulfilment:features": {
      read: "fulfilment.read_features",
      create: false,
      edit: false,
    },
    submissions: {
      read: "staff.rolling.read",
      create: "staff.rolling.write",
      edit: "staff.rolling.write",
    },
    fulfilment: {
      read: [
        "fulfilment.read_subscriptions",
        "fulfilment.read_packages",
        "fulfilment.read_features",
      ],
      create: ["fulfilment.create_subscription", "fulfilment.create_package"],
      edit: ["fulfilment.edit_subscription", "fulfilment.create_package"],
    },
  };

  const messageConfig: Record<string, MessageConfig> = {
    users: {
      readOnly:
        "Company user accounts are managed externally and cannot be edited through this UI.",
    },
    "fulfilment:features": {
      readOnly: "Features are read-only and cannot be created or edited through this UI.",
    },
    "fulfilment:subscriptions": {
      readOnly:
        "You can view data but cannot create or edit. Contact your administrator for write access.",
      limited:
        "You can create subscriptions but cannot edit or cancel them. Contact your administrator for edit access.",
    },
    default: {
      readOnly:
        "You can view data but cannot create or edit. Contact your administrator for write access.",
      limited:
        "You can create items but cannot edit or delete them. Contact your administrator for full access.",
    },
  };

  const configKey = scope === "fulfilment" && resource ? `${scope}:${resource}` : scope;
  const config = permissionConfig[configKey];

  if (!config) {
    return null;
  }

  const canRead = Array.isArray(config.read)
    ? hasAnyPermission(config.read)
    : typeof config.read === "string"
      ? hasPermission(config.read)
      : false;

  const canCreate = Array.isArray(config.create)
    ? hasAnyPermission(config.create)
    : typeof config.create === "string"
      ? hasPermission(config.create)
      : config.create;

  const canEdit = Array.isArray(config.edit)
    ? hasAnyPermission(config.edit)
    : typeof config.edit === "string"
      ? hasPermission(config.edit)
      : config.edit;

  let variant: "warning" | "error" | "info" | null = null;
  let label = "";
  let message = "";

  if (!canRead) {
    variant = "error";
    label = "No Access";
    message = "You don't have permission to view this data. Please contact your administrator.";
  } else if (canRead && !canCreate && !canEdit) {
    variant = "info";
    label = "Read-Only Access";
    const msgConfig = messageConfig[configKey] || messageConfig.default;
    message = msgConfig.readOnly;
  } else if (canRead && canCreate && !canEdit) {
    variant = "warning";
    label = "Limited Permissions";
    const msgConfig = messageConfig[configKey] || messageConfig.default;
    message = msgConfig.limited || messageConfig.default.limited!;
  }

  if (!variant) {
    return null;
  }

  return (
    <div className={styles.badge} data-variant={variant} role="status">
      <svg
        className={styles.badgeIcon}
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
        />
      </svg>
      <span className={styles.badgeLabel}>{label}</span>
      <div className={styles.tooltip}>{message}</div>
    </div>
  );
}
